import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ButtonProps, Grid, Typography } from '@mui/material'
import getOr from 'lodash/fp/getOr'
import pickBy from 'lodash/fp/pickBy'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import Icon from '@mui/material/Icon'
import { VinText } from '@carsys/ui'
import { IVehicleType, ResultObjectType } from '@cbgms/api'
import LicensePlate from '@cbgms/components/form/LicensePlate'
import { IGroup } from '@cbgms/base/utils/helpers'
import { ResultTitle } from './Title'
import { manufacturerToClass } from '@cbgms/components/src/utils/vehicle'
import { useOnEnterHandler } from '@carsys/ui/src/helpers/keyboard/use-on-enter-handler'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%'
    },
    pointer: {
      cursor: 'pointer'
    }
  })
)

export interface VehicleResultProps {
  Type: ResultObjectType
  Vehicle: IGroup<IVehicleType>
  selected?: boolean
  clearQuery: () => void
  primaryAction?: () => Promise<void>
  onSelect?: (vehicle: IGroup<IVehicleType>) => void
  ActionButton?: React.FC<ActionButtonProps>
  hasGroupHeader: boolean
}
export interface ActionButtonProps extends ButtonProps {
  hasMultipleMatches: boolean
}

interface StopPropagationEvent {
  stopPropagation: () => void
}

const useGetIdenticalVehicleAttributes = (vehicle: IGroup<IVehicleType>) =>
  React.useMemo(
    () =>
      pickBy((value, key) => !vehicle.Values.find(genericVehicle => getOr(undefined, key, genericVehicle) !== value), vehicle.Values[0]),
    [vehicle]
  )

export const VehicleResult: React.FC<VehicleResultProps> = props => {
  const { Vehicle, Type, primaryAction, onSelect, ActionButton, hasGroupHeader } = props

  const classes = useStyles()
  const { t } = useTranslation()
  const identicalVehicleAttributes = useGetIdenticalVehicleAttributes(Vehicle)
  const gridSize = React.useMemo(
    () => ({
      vehicleCell: ActionButton ? 6 : 12,
      actionCell: ActionButton ? 6 : 0
    }),
    [ActionButton]
  )
  const handleSelection = (e: StopPropagationEvent) => {
    if (onSelect) {
      onSelect(Vehicle)
      return
    }
    e?.stopPropagation()
    primaryAction?.()
  }
  useOnEnterHandler(props.selected, handleSelection)

  const firstVehicle = Vehicle.Values[0]
  const hasMultipleMatches = Vehicle.Values.length > 1

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        <Grid data-testid='select-vehicle-button' item xs={gridSize.vehicleCell} onClick={handleSelection} className={classes.pointer}>
          <Grid container spacing={0}>
            <ResultTitle resultType={Type} isTitleVisible={!hasGroupHeader} />
          </Grid>
          <Box display='flex' alignItems='center'>
            <Box display='inline-block'>
              {firstVehicle.Manufacturer && (
                <Box display='inline-flex' alignContent='center' mr={1}>
                  <Icon className={`icon vehicle brand ${manufacturerToClass(firstVehicle.Manufacturer)}`} fontSize='large' />
                </Box>
              )}
              {firstVehicle.Registration && (
                <Box mr={1} display='inline-block'>
                  <LicensePlate registration={firstVehicle.Registration} type={firstVehicle.AssignedAs} />
                </Box>
              )}
            </Box>
            <Box flex='1 1 auto' display='inline-block'>
              <Box display='inline-block'>
                {hasMultipleMatches && (
                  <Typography variant='body2'>
                    {Vehicle.Values.length} {t('vehicle.matching_vehicles_found')}
                  </Typography>
                )}
                <Typography variant='body2'>
                  {`${identicalVehicleAttributes?.Manufacturer} ${identicalVehicleAttributes?.Model || ''} ${
                    identicalVehicleAttributes?.Type || ''
                  }`}
                </Typography>
                {identicalVehicleAttributes.Vin ? <VinText value={identicalVehicleAttributes.Vin} /> : null}
              </Box>
            </Box>
          </Box>
        </Grid>
        {!!ActionButton && (
          <Grid item xs={gridSize.actionCell}>
            <Box height='100%' justifyContent='end' display='flex' alignItems='center'>
              <ActionButton hasMultipleMatches={hasMultipleMatches} onClick={handleSelection} />
            </Box>
          </Grid>
        )}
      </Grid>
    </div>
  )
}
